/**
 * Modal: release a deployed network to Agentic Networks.
 *
 * Two modes (step 2.2):
 *   - NEW_POOL: creates a brand-new customer pool at the next release version.
 *   - PATCH_EXISTING_POOLS: advances the release pointer on selected pools
 *     in place. Members stay; running conversations continue.
 *
 * All decision logic lives in ``utils/releaseDialog.js`` so it's unit-tested
 * without React — the JSX below is a thin shell.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { releaseNetwork, listNetworkPools } from '@/services/agenticNetworksApi';
import { listNetworkReleases, getNextReleaseLabel } from '@stepflow/ui-shared/services/networkReleasesApi';
import { listNetworkSnapshots } from '@/services/networkRestoreApi';
import VersionSnapshotPicker from './VersionSnapshotPicker.jsx';
import {
  RELEASE_MODE_NEW_POOL,
  RELEASE_MODE_PATCH_EXISTING_POOLS,
  availableReleaseModes,
  canSubmitRelease,
  composeReleaseSubmitPayload,
  patchablePools,
  pickDefaultReleaseMode,
} from '@/utils/releaseDialog';
import {
  DEFAULT_TRIGGER_MODE,
  listTriggerModes,
} from '@stepflow/ui-shared/utils/networkTrigger.js';
import { displayVersionLabel, predictNextVersionLabel, majorFromLabel } from '@stepflow/ui-shared/utils/stepflowVersionLabel';
import { WORKBENCH_OUTLINE_BTN_MODAL_LG } from '@/utils/workbenchOutlineButtonClassNames.js';
import ReleaseVersionHistory from './ReleaseVersionHistory';
import SupervisorMemberPicker from './SupervisorMemberPicker.jsx';
import CollapsibleSection from '@stepflow/ui-shared/components/CollapsibleSection.jsx';
import WorkspaceMembersMultiPicker from './WorkspaceMembersMultiPicker.jsx';
import ExpertTeamDeployOption from './ExpertTeamDeployOption.jsx';
import { deployExpertTeam, stageLeadName, stageNetworkRelease } from '@stepflow/ui-shared/services/expertDeployment';

// Default room-name suggestion pool. Large + varied so the pre-filled name rarely
// collides with an existing room on the network (the real uniqueness guard is the
// backend 409; this just makes the first suggestion usually free). Backend mirror:
// api/pet_names.py::_ANIMAL_FIRST_NAMES.
const DEFAULT_POOL_SUGGESTIONS = [
  'luca', 'lara', 'milo', 'nora', 'kai', 'zia', 'bella', 'nala', 'simba', 'remy',
  'koda', 'kira', 'loki', 'nova', 'atlas', 'bowie', 'cleo', 'dara', 'echo', 'finn',
  'greta', 'hazel', 'indy', 'juno', 'leo', 'mila', 'nico', 'ollie', 'pippa', 'quinn',
  'rio', 'stella', 'tala', 'uma', 'vera', 'willa', 'xena', 'yuki', 'zara', 'archie',
  'basil', 'cosmo', 'daisy', 'elsa', 'freya', 'gus', 'honey', 'ivy', 'jax', 'kona',
  'lily', 'maple', 'nori', 'onyx', 'pearl', 'ruby', 'sage', 'theo', 'ursa', 'wally',
  'zuri', 'amber', 'biscuit', 'clover', 'dash', 'ember', 'ginger', 'hero', 'kenji', 'lotus',
  'misty', 'nimbus', 'opal', 'percy', 'quill', 'rocket', 'sky', 'tango', 'viola', 'willow',
  'xander', 'yarrow', 'ziggy', 'aspen', 'birch', 'cedar', 'dune', 'fern', 'haven', 'iris',
  'jasper', 'koa', 'lyra', 'mika', 'orion', 'poppy', 'reef', 'tess', 'vale', 'zephyr',
  'arlo', 'bruno', 'coco', 'duke', 'enzo', 'gizmo', 'hugo', 'jett', 'luna', 'otis',
  'penny', 'rex', 'sadie', 'toby', 'winnie', 'zeke', 'juniper', 'marlow', 'silas', 'wilder',
];

function pickDefaultPoolName() {
  return DEFAULT_POOL_SUGGESTIONS[Math.floor(Math.random() * DEFAULT_POOL_SUGGESTIONS.length)];
}

export default function ReleaseNetworkDialog({
  open,
  // EXPERT-ROOMS §15 — opened from "Deploy the QA / Production team": a team of rooms for THAT stage, preselected
  initialExpertStage = '',
  onClose,
  networkId,
  networkName,
  networkReleaseVersion,
  network,
  onReleased,
  // Optional hint from the caller about the nature of the last build:
  // ``'structural'`` (roster/topology/edges → new SFN → MAJOR) vs
  // ``'prompt-refresh'`` (re-render prompts only → same SFN → MINOR). See
  // §2.2. Used ONLY for the pre-server prediction placeholder; the
  // authoritative label from GET /next-release-label always wins once it
  // lands (it runs a byte-for-byte SFN compare). When absent, we predict
  // the conservative MINOR (the common config-only case) so a prompt-refresh
  // never flashes a scary "2.00" that then drops back to "1.10".
  changeType,
}) {
  const { user, activeTenantId } = useAuth();
  const currentUserEmail = (user?.email || '').trim().toLowerCase();

  const [pools, setPools] = useState([]);
  const [poolsLoading, setPoolsLoading] = useState(false);
  const [poolsError, setPoolsError] = useState(null);
  const [mode, setMode] = useState(RELEASE_MODE_NEW_POOL);
  const [poolName, setPoolName] = useState('');
  const [selectedPoolIds, setSelectedPoolIds] = useState([]);
  // ROOM AGENT-OVERRIDE PRESERVATION (ROOM-SCOPES.md): pools the user OPTED OUT of keeping their
  // owner agent disable/bypass carve across this upgrade (default = keep). These take the new network
  // graph fresh. Cleared for a pool when it's deselected.
  const [resetOverridePoolIds, setResetOverridePoolIds] = useState([]);
  const toggleKeepOverrides = (poolId) =>
    setResetOverridePoolIds((prev) =>
      prev.includes(poolId) ? prev.filter((p) => p !== poolId) : [...prev, poolId]);
  // NEW_POOL governance triplet (Phase A): owner + co-supervisors + trigger.
  // The owner defaults to the releaser (familiar Workbench pattern: "I am
  // also the supervisor unless I say otherwise"); supervisors stays empty
  // by default; trigger defaults to HYBRID — the most permissive mode and
  // what the system did implicitly before this field existed.
  const [ownerEmail, setOwnerEmail] = useState('');
  const [supervisorEmails, setSupervisorEmails] = useState([]);
  const [triggerMode, setTriggerMode] = useState(DEFAULT_TRIGGER_MODE);
  // Pre-bake the network's skills into a workspace for the new room (default ON).
  // Backend also gates on builder-present + skills-exist, so this is a no-op when
  // there's nothing to bake (doc/arch/vertical/SKILLS.md).
  const [bootstrapWorkspace, setBootstrapWorkspace] = useState(true);
  // "Start rooms with the suggested plan" (owner 2026-10-21) — OFF by default: rooms start lean and plan when their
  // people choose to. On → the new room begins at Plan with the network's suggestion, still fully its own to change.
  const [startWithSuggestedPlan, setStartWithSuggestedPlan] = useState(false);
  const hasSuggestedPlan = !!(network?.suggestedPlan?.milestones?.length || network?.milestoneHints?.length);
  // How this room's connections are provisioned at release (backend flag: connectionMode).
  //   'off'     — provision NOTHING and don't inherit the network's credentials; agents start
  //               blank, operator assigns per-agent in the Connections tab. DEFAULT.
  //   'room'    — seed the room its own connection shells to credential (own copies).
  //   'network' — inherit the network's connections as-is.
  const [connectionMode, setConnectionMode] = useState('off');
  // §13 Lead/linked (PROJECT networks): 'lead' opens a fresh instance (own ledger + workspace);
  // 'linked' + leadPoolId joins a chosen Lead's instance (shared ledger + workspace, load-balanced).
  const [roomKind, setRoomKind] = useState('lead');
  const [leadPoolId, setLeadPoolId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // EXPERT ROOMS (EXPERT-ROOMS.md §4): an Expert Network deploys as a team of rooms (default on until it has one)
  const [expertTeam, setExpertTeam] = useState(false);
  const [expertStage, setExpertStage] = useState('dev');   // EXPERT-ROOMS §15 — which stage the team serves
  const [expertProgress, setExpertProgress] = useState([]);
  // the deployment as it stands after a partial run — a retry reuses its Lead (the network prop may be stale)
  const [expertDeploymentNow, setExpertDeploymentNow] = useState(null);
  useEffect(() => {
    if (open) {
      if (initialExpertStage && network?.expertNetwork) {
        setMode(RELEASE_MODE_NEW_POOL); setExpertTeam(true); setExpertStage(initialExpertStage);
        setExpertDeploymentNow(null);
      } else {
        setExpertTeam(!!network?.expertNetwork && !network?.expertDeployment?.leadPoolId);
      }
      setExpertProgress([]);
    }
  }, [open, initialExpertStage, network?.expertNetwork, network?.expertDeployment?.leadPoolId]);
  const [error, setError] = useState(null);
  // EXPERT-ROOMS §15 — a stage team's Lead is named for its stage: ``<name>-dev`` / ``<name>-qa`` / ``<name>`` (prod)
  useEffect(() => {
    if (!open || !expertTeam || !network?.expertNetwork) return;
    const devLead = (network?.expertDeployments || {}).dev?.leadPoolId || network?.expertDeployment?.leadPoolId || '';
    // a team deployed before stage names (``lily`` as dev) keeps its id — Production then can't reuse it: ``-prod``
    setPoolName((cur) => {
      const n = stageLeadName(devLead || cur, expertStage);
      return n === devLead && expertStage !== 'dev' ? `${n}-prod` : n;
    });
  }, [open, expertTeam, expertStage]); // eslint-disable-line react-hooks/exhaustive-deps

  // NEW_POOL form is structured as collapsible sections mirroring the room
  // settings drawer (Members / Trigger mode / Room context). "Basics" (Version
  // + Pool name) opens expanded; governance + tools sections start collapsed
  // and pre-filled (most releases keep the defaults — owner=releaser,
  // trigger=HYBRID, workspace+connections on). The user expands only what they
  // want to change. ``id`` = drawer section key so the wording stays in sync.
  const [openSection, setOpenSection] = useState({
    basics: true,
    members: false,
    trigger: false,
    roomContext: false,
  });
  const toggleSection = (key) =>
    setOpenSection((s) => ({ ...s, [key]: !s[key] }));

  // Version + Snapshot picker state. Loaded in parallel with pools when
  // the dialog opens. ``versionRef`` is ``"vN"`` (or empty when the
  // network has no releases yet — the backend cuts the next version).
  // ``snapshotRef`` is the synthetic ``"{kind}::{capturedAt}"`` key.
  const [versions, setVersions] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState(null);
  const [vspValue, setVspValue] = useState({ versionRef: '', snapshotRef: '' });
  const [nextLabelFromServer, setNextLabelFromServer] = useState(null);

  const triggerOptions = useMemo(() => listTriggerModes(), []);

  // The workspace bootstrap only applies to a network with a builder agent — a
  // workspace is the builder's on-disk tree (doc/arch/vertical/SKILLS.md). Pure
  // worker networks have no builder (and we don't author skills for them), so
  // hide the checkbox entirely there. ``network.agents`` is a {logicalId: cfg}
  // map; UNIVERSAL_BUILDER is the builder type.
  const hasBuilderAgent = useMemo(() => {
    const agents = (network && typeof network.agents === 'object') ? network.agents : {};
    return Object.values(agents || {}).some(
      (a) => String((a || {}).type || '').toUpperCase() === 'UNIVERSAL_BUILDER',
    );
  }, [network]);

  // SHARED NETWORK WORKSPACE (CORE-AGENTS.md §4.4): the workspace the network creator
  // assigned on the Shared (``__roomContext__``) row. When set, the release backend PINS it
  // room-wide and SKIPS the per-room skills-bootstrap — so the bootstrap checkbox below is
  // moot. We disable it (+ a note) so the UI reflects what the backend will actually do.
  const networkWorkspaceId = useMemo(() => {
    const agents = (network && typeof network.agents === 'object') ? network.agents : {};
    const rc = agents.__roomContext__ || {};
    return String(rc.defaultWorkspaceId || '').trim();
  }, [network]);

  // §13 Lead/linked rooms: the selector appears once the network ALREADY has ≥1 room (the first room is a silent
  // Lead — no prompt). ``leadRooms`` are the pickable anchors (a room is a Lead iff its ``leadPoolId`` is empty).
  // Linked rooms may only link to a Lead (2-level).
  const leadRooms = useMemo(
    () => (pools || []).filter((p) => !String(p?.leadPoolId || '').trim()),
    [pools],
  );
  // PRODUCT.md §2 (R2c): ANY network's rooms may link — a room plans by choice, not by build-time phases.
  const showRoomKind = (pools || []).length > 0;
  // A linked room shares the Lead's workspace — lock the bootstrap UI exactly like a network
  // workspace does (the backend pins the Lead's ws + skips bootstrap).
  const workspaceLockedToLead = showRoomKind && roomKind === 'linked';

  // Load existing pools + Versions + Snapshots on open. The three
  // requests are independent so we fire them in parallel; each
  // updates its own slice of state and the dialog tolerates partial
  // load (e.g. snapshots fail → picker shows "no snapshots" but the
  // pool flow still works).
  useEffect(() => {
    if (!open || !networkId) return undefined;
    let cancelled = false;
    setPoolsLoading(true);
    setPoolsError(null);
    setPoolName(pickDefaultPoolName());
    setSelectedPoolIds([]);
    setError(null);
    setOwnerEmail(currentUserEmail);
    setSupervisorEmails([]);
    setTriggerMode(DEFAULT_TRIGGER_MODE);
    setVersions([]);
    setSnapshots([]);
    setVersionsLoading(true);
    setVersionsError(null);
    setVspValue({ versionRef: '', snapshotRef: '' });
    setNextLabelFromServer(null);
    getNextReleaseLabel(networkId)
      .then((res) => {
        if (cancelled) return;
        if (res && typeof res.label === 'string' && res.label) {
          setNextLabelFromServer({ label: res.label, kind: res.kind });
        }
      })
      .catch(() => { /* fall back to local prediction */ });

    listNetworkPools(networkId)
      .then(({ pools: fetched }) => {
        if (cancelled) return;
        setPools(fetched);
        setMode(pickDefaultReleaseMode(fetched));
      })
      .catch((err) => {
        if (cancelled) return;
        setPoolsError(err.message || 'Failed to load pools');
        setPools([]);
        setMode(RELEASE_MODE_NEW_POOL);
      })
      .finally(() => {
        if (!cancelled) setPoolsLoading(false);
      });

    // Section 3: fetch the Version + Snapshot lists in parallel. The
    // releases endpoint returns active + deactivated rows; the picker
    // filters out deactivated. The snapshots endpoint returns the
    // unified baseline / release-baseline / user / auto rows already
    // tagged with ``parentVersion`` (Section 2).
    Promise.allSettled([
      listNetworkReleases(networkId, { limit: 50 }),
      listNetworkSnapshots(networkId),
    ]).then(([rRes, sRes]) => {
      if (cancelled) return;
      if (rRes.status === 'fulfilled') {
        setVersions(Array.isArray(rRes.value?.releases) ? rRes.value.releases : []);
      } else {
        setVersionsError(rRes.reason?.message || 'Failed to load Versions');
      }
      if (sRes.status === 'fulfilled') {
        setSnapshots(Array.isArray(sRes.value?.snapshots) ? sRes.value.snapshots : []);
      }
      setVersionsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [open, networkId, currentUserEmail]);

  const modes = useMemo(() => availableReleaseModes(pools), [pools]);
  // The next release version targets current + 1; use null if unknown so
  // the "behind" indicator gracefully degrades to "always patchable".
  const nextVersion = Number.isFinite(networkReleaseVersion)
    ? Number(networkReleaseVersion) + 1
    : null;
  // The version-picker needs a concrete vN+1 even when the network has
  // no releases yet (so "Cut new (v1)" is always offerable). Derive from
  // the loaded ``versions`` list, falling back to ``nextVersion`` from
  // the network row, then to 1.
  const nextVersionForPicker = useMemo(() => {
    const max = (versions || [])
      .map((r) => Number(r?.releaseVersion))
      .filter((n) => Number.isFinite(n) && n >= 1)
      .reduce((a, b) => Math.max(a, b), 0);
    if (max >= 1) return max + 1;
    if (Number.isFinite(nextVersion) && Number(nextVersion) >= 1) return Number(nextVersion);
    return 1;
  }, [versions, nextVersion]);
  const isNoop = nextLabelFromServer?.kind === 'NOOP';
  // Pre-server prediction only. ``releaseEligible`` is coarse — it's true for
  // ANY pending edit, so on its own it can't tell a prompt-refresh (same SFN
  // → MINOR) from a structural revise (new SFN → MAJOR). When the caller
  // knows the change kind we honor it exactly; otherwise we keep the old
  // coarse heuristic. Either way the authoritative label from
  // GET /next-release-label (byte-for-byte SFN compare) wins once it lands.
  // §4.1 Bug C.
  const normalizedChangeType = String(changeType || '').trim().toLowerCase();
  const topologyChanged = (() => {
    if (isNoop) return false;
    if (normalizedChangeType === 'prompt-refresh') return false; // same SFN → MINOR
    if (normalizedChangeType === 'structural') return true;      // new SFN → MAJOR
    return network?.releaseEligible !== false;                   // unknown → coarse heuristic
  })();
  const nextVersionLabel = useMemo(() => {
    if (nextLabelFromServer?.label) return nextLabelFromServer.label;
    return predictNextVersionLabel(versions, topologyChanged);
  }, [nextLabelFromServer, versions, topologyChanged]);

  // §13 linked rooms — the semantic MAJOR is the left of the version label ("1.10" → 1).
  // A linked room shares its Lead's PLAN, which is the MAJOR (topology); the MINOR (config)
  // may differ. So we only offer Lead rooms on the SAME major as the version we'd release,
  // and pin a linked room to the selected Lead's version.
  const targetMajor = useMemo(() => majorFromLabel(nextVersionLabel), [nextVersionLabel]);
  const leadLabelOf = useCallback(
    (p) =>
      displayVersionLabel({ releaseVersion: p?.releaseVersion }, versions)
      || (p?.releaseVersion ? `v${p.releaseVersion}` : ''),
    [versions],
  );
  const linkableLeadRooms = useMemo(() => {
    if (targetMajor == null) return leadRooms;
    return leadRooms.filter((p) => {
      const lm = majorFromLabel(leadLabelOf(p));
      return lm == null || lm === targetMajor; // drop only a KNOWN major mismatch
    });
  }, [leadRooms, targetMajor, leadLabelOf]);
  const selectedLead = useMemo(
    () => linkableLeadRooms.find((p) => p.poolId === leadPoolId) || null,
    [linkableLeadRooms, leadPoolId],
  );
  const selectedLeadLabel = useMemo(
    () => (selectedLead ? leadLabelOf(selectedLead) : ''),
    [selectedLead, leadLabelOf],
  );
  // Pin a linked room's release to the chosen Lead's version (its plan). Clear a stale
  // selection that got filtered out by the major gate.
  useEffect(() => {
    if (roomKind !== 'linked') return;
    if (selectedLead) {
      // versionRef wire format is ``vN`` (backend rejects a bare integer).
      const rv = Number(selectedLead.releaseVersion);
      setVspValue({ versionRef: rv >= 1 ? `v${rv}` : '', snapshotRef: '' });
    } else if (leadPoolId) {
      setLeadPoolId('');
    }
  }, [roomKind, selectedLead, leadPoolId]);

  // Highest already-released version (active + deactivated). This is the
  // target for the "upgrade a room to an existing newer version" path — the
  // first-ever MAJOR-upgrade case (e.g. room on 1.00 while 2.00 already
  // exists). See doc/arch/vertical/NETWORK-TO-ROOMS.md §4.1.
  const maxExisting = useMemo(() => (versions || [])
    .map((r) => Number(r?.releaseVersion))
    .filter((n) => Number.isFinite(n) && n >= 1)
    .reduce((a, b) => Math.max(a, b), 0), [versions]);

  // Can we cut a genuinely NEW version? Only when the server says something
  // changed (not NOOP) and the network row has pending changes. When we
  // CAN'T, PATCH mode falls back to advancing behind rooms to ``maxExisting``.
  const hasPendingReleaseChanges = !isNoop && network?.pendingReleaseChanges !== false;
  const patchTargetVersion = hasPendingReleaseChanges
    ? nextVersion
    : (maxExisting >= 1 ? maxExisting : nextVersion);
  const patchTargets = useMemo(
    () => patchablePools(pools, patchTargetVersion),
    [pools, patchTargetVersion],
  );

  // Label for whatever the patch will land the rooms on: the predicted next
  // label when cutting new, else the existing target version's label.
  const patchTargetLabel = hasPendingReleaseChanges
    ? nextVersionLabel
    : (displayVersionLabel(patchTargetVersion, versions) || nextVersionLabel);

  // Are we upgrading selected rooms to an ALREADY-existing version? True only
  // when we can't cut new AND at least one SELECTED room is behind the
  // existing target. This opens the otherwise-disabled Release button for the
  // MAJOR-upgrade case without loosening the no-op guard for fresh cuts.
  const selectedBehindExisting = !hasPendingReleaseChanges
    && maxExisting >= 1
    && patchTargets.some((p) => selectedPoolIds.includes(p.poolId) && p.isBehind);

  const submitDecision = canSubmitRelease({
    mode,
    poolName,
    selectedPoolIds,
    availablePools: pools,
    // Server is authoritative: if it ran live SFN-hash + agent-config-hash
    // compare and decided "nothing changed", PATCH must be blocked even
    // when ``pendingReleaseChanges`` is stale-true on the network row —
    // UNLESS we're upgrading a behind room to an existing newer version.
    hasPendingReleaseChanges,
    isNoop,
    isUpgradeToExisting: selectedBehindExisting,
  });

  const togglePoolSelection = (poolId) => {
    setSelectedPoolIds((prev) => {
      const next = prev.includes(poolId) ? prev.filter((p) => p !== poolId) : [...prev, poolId];
      // Deselecting a pool clears any "reset overrides" flag it carried (keep state tidy).
      if (!next.includes(poolId)) {
        setResetOverridePoolIds((r) => r.filter((p) => p !== poolId));
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (mode === RELEASE_MODE_NEW_POOL && expertTeam && network?.expertNetwork) {
      setSubmitting(true);
      setExpertProgress([]);
      const seen = {};
      // every room of the team gets the dialog's OTHER settings (owner, supervisors, trigger, connections, workspace)
      let base = {};
      try {
        const p = composeReleaseSubmitPayload({ mode, poolName, selectedPoolIds, ownerEmail, supervisorEmails,
          trigger: triggerMode, bootstrapWorkspace, connectionMode, roomKind: 'lead', leadPoolId: '' }) || {};
        const { poolName: _pn, roomKind: _rk, leadPoolId: _lp, mode: _m, ...rest } = p;
        base = rest;
      } catch { base = {}; }
      try {
        const { failed, deployment: depNow } = await deployExpertTeam(networkId, {
          base, leadName: poolName, stage: expertStage === 'dev' ? '' : expertStage,
          deployment: expertDeploymentNow || (network?.expertDeployments || {})[expertStage]
            || (expertStage === 'dev' ? network?.expertDeployment : null) || null,
          // a QA / Production team runs the NETWORK release its training release was cut on (the composite version's
          // left half) — never "whatever is newest"
          versionRef: (expertStage !== 'dev' && stageNetworkRelease(network, expertStage))
            ? `v${stageNetworkRelease(network, expertStage)}` : (vspValue?.versionRef || ''),
          onProgress: (st) => { seen[st.team || ''] = st; setExpertProgress(Object.values(seen)); },
        });
        if (depNow) setExpertDeploymentNow(depNow);
        if (failed.length) {
          // stay open so the person sees it and retries (onReleased would close the dialog)
          setError(`Some department rooms weren't created (${failed.map((f) => f.team).join(', ')}) — submit again to retry them.`);
          return;
        }
        onReleased?.();
        onClose();
      } catch (err) {
        setError(err.message || 'Deploy failed');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    // §13 — a Linked room must name its Lead (the backend 400s otherwise; catch it here for a clean msg).
    if (mode === RELEASE_MODE_NEW_POOL && showRoomKind && roomKind === 'linked' && !leadPoolId) {
      setError('Pick a Lead room to link to (or choose "New room").');
      return;
    }
    let payload;
    try {
      payload = composeReleaseSubmitPayload({
        mode,
        poolName,
        selectedPoolIds,
        ownerEmail,
        supervisorEmails,
        trigger: triggerMode,
        bootstrapWorkspace,
        connectionMode,
        startWithSuggestedPlan: hasSuggestedPlan && !(showRoomKind && roomKind === 'linked') && startWithSuggestedPlan,
        // §13 — only forwarded (and validated) for a linked room on a project network.
        roomKind: showRoomKind ? roomKind : 'lead',
        leadPoolId: showRoomKind && roomKind === 'linked' ? leadPoolId : '',
        // Upgrade-to-existing-version (MAJOR upgrade): pin the patch to the
        // highest already-released version so the backend reuses its SFN
        // version + ARN instead of cutting a new one.
        isUpgradeToExisting: selectedBehindExisting,
        versionRef: selectedBehindExisting ? `v${maxExisting}` : '',
        // ROOM AGENT-OVERRIDE PRESERVATION (ROOM-SCOPES.md): pools the user unchecked "Keep agent
        // overrides" for → reset to the network graph. Default (empty) = preserve every patched room.
        resetAgentOverridesPoolIds: resetOverridePoolIds,
      });
    } catch (err) {
      setError(err.message);
      return;
    }
    // Section 3: forward the Version + Snapshot picks alongside the
    // existing payload. The backend treats them as advisory in this
    // section (validates shape but still uses the canonical
    // "latest released Version" path); Section 4 wires the picks to
    // the alias retarget / pool ReleaseVersion stamp.
    if (mode === RELEASE_MODE_NEW_POOL && vspValue?.versionRef) {
      payload = {
        ...payload,
        versionRef: vspValue.versionRef,
        ...(vspValue.snapshotRef ? { snapshotRef: vspValue.snapshotRef } : {}),
      };
    }
    setSubmitting(true);
    try {
      const res = await releaseNetwork(networkId, payload);
      // Backend returns ``200 { noop: true }`` when neither topology nor
      // agent config has changed since the previous release AND the request
      // didn't pin a ``versionRef`` to an existing version. Nothing was
      // actually written — we must NEVER report success, in EITHER mode.
      // (Regression: this guard used to be scoped to NEW_POOL, so a PATCH
      // that no-op'd fell through to onReleased()/onClose() and the UI lied
      // about success while the room stayed on its old version.)
      if (res && res.noop === true) {
        setError(
          res.message
          || (mode === RELEASE_MODE_PATCH_EXISTING_POOLS
            ? 'Nothing to apply — the selected room is already on the latest version, or no version gap was detected.'
            : 'No changes since the last release — pick the existing version above to open a new pool against it.'),
        );
        return;
      }
      onReleased?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Release failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.button
            type="button"
            aria-label="Close"
            className="absolute inset-0 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !submitting && onClose()}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="release-dialog-title"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            className="relative z-[101] flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
          >
            {/* Scrollable body — the footer (Cancel/Release) is pinned OUTSIDE this. */}
            <div className="min-h-0 flex-1 overflow-y-auto p-6">
            <h2 id="release-dialog-title" className="text-lg font-semibold text-gray-900">
              Create a Room
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              This freezes <span className="font-medium text-gray-800">{networkName}</span> as a versioned
              snapshot.
              {' '}Choose whether to patch existing pools in place or open a new one.
            </p>

            <form id="release-network-form" onSubmit={handleSubmit} className="mt-4">
              {/* Mode selector — radios, not tabs, to make the invariant
                  (exactly one mode at a time) explicit in the DOM. */}
              <fieldset className="space-y-3" disabled={submitting}>
                <legend className="sr-only">Release mode</legend>
                <label
                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${
                    mode === RELEASE_MODE_PATCH_EXISTING_POOLS
                      ? 'border-amber-400 bg-amber-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  } ${!modes.canPatch ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="radio"
                    name="release-mode"
                    value={RELEASE_MODE_PATCH_EXISTING_POOLS}
                    checked={mode === RELEASE_MODE_PATCH_EXISTING_POOLS}
                    disabled={!modes.canPatch}
                    onChange={() => setMode(RELEASE_MODE_PATCH_EXISTING_POOLS)}
                    className="mt-0.5"
                  />
                  <span className="text-sm">
                    <span className="block font-medium text-gray-900">
                      Patch existing pool(s)
                    </span>
                    <span className="block text-gray-600">
                      Advance selected pools to the new version. Members stay; running
                      conversations continue.
                    </span>
                  </span>
                </label>

                <label
                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${
                    mode === RELEASE_MODE_NEW_POOL
                      ? 'border-emerald-400 bg-emerald-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="release-mode"
                    value={RELEASE_MODE_NEW_POOL}
                    checked={mode === RELEASE_MODE_NEW_POOL}
                    onChange={() => setMode(RELEASE_MODE_NEW_POOL)}
                    className="mt-0.5"
                  />
                  <span className="text-sm">
                    <span className="block font-medium text-gray-900">
                      Release to a new pool
                    </span>
                    <span className="block text-gray-600">
                      Open a parallel customer room at the new version. Doesn't touch
                      existing pools.
                    </span>
                  </span>
                </label>
              </fieldset>

              {/* Mode-specific body */}
              {mode === RELEASE_MODE_NEW_POOL ? (
                <div className="mt-4 overflow-hidden rounded-lg border border-gray-200">
                  {/* BASICS — Version & Snapshot + Pool name. Expanded by
                      default (the only required input lives here). No drawer
                      equivalent, so it's a plain top section. */}
                  <CollapsibleSection
                    id="basics"
                    title="Basics"
                    subtitle="Version to release · room name"
                    open={openSection.basics}
                    onToggle={() => toggleSection('basics')}
                    summary={poolName ? `${poolName} · ${nextVersionLabel || 'next'}` : 'Name required'}
                  >
                    <div className="space-y-4 py-1">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Version & Snapshot
                        </label>
                        <div className="mt-2">
                          {roomKind === 'linked' && selectedLead ? (
                            // Linked room joins the Lead's plan → its version is PINNED to the
                            // Lead's (same major/topology). Show it read-only, not the picker.
                            <div
                              className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                              data-testid="linked-room-pinned-version"
                            >
                              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                Version
                              </span>
                              <div className="mt-0.5 text-base font-semibold text-gray-900">
                                {selectedLeadLabel}
                              </div>
                              <p className="mt-1 text-xs text-gray-500">
                                Pinned to Lead room{' '}
                                <span className="font-medium">{selectedLead.name || selectedLead.poolId}</span>{' '}
                                — a linked room joins its plan on the same version.
                              </p>
                            </div>
                          ) : (
                            <VersionSnapshotPicker
                              releases={versions}
                              snapshots={snapshots}
                              nextVersion={nextVersionForPicker}
                              value={vspValue}
                              onChange={setVspValue}
                              loading={versionsLoading}
                              error={versionsError}
                              topologyChanged={network?.releaseEligible !== false}
                              nextLabel={nextVersionLabel}
                            />
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700" htmlFor="poolName">
                          Room name
                        </label>
                        <input
                          id="poolName"
                          type="text"
                          value={poolName}
                          onChange={(ev) => setPoolName(ev.target.value)}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="e.g. luca"
                          autoComplete="off"
                          disabled={submitting}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Becomes the pool identifier (letters, digits, dashes; 3-40 chars).
                        </p>
                      </div>
                      <ExpertTeamDeployOption network={network} checked={expertTeam} onChange={setExpertTeam}
                        progress={expertProgress} disabled={submitting} stage={expertStage}
                        onStage={(st) => { setExpertStage(st); setExpertDeploymentNow(null); }} />
                      {/* §13 — PROJECT networks only, and only once a room exists (the first room is a
                          silent Lead). New room = a fresh project instance (own plan + workspace);
                          Linked room = joins a chosen Lead's instance (shared plan + workspace,
                          load-balanced). Links may only point at a Lead room. */}
                      {showRoomKind ? (
                        <fieldset className="rounded-lg border border-gray-200 p-3">
                          <legend className="px-1 text-sm font-medium text-gray-900">Project instance</legend>
                          <div className="mt-1 space-y-2">
                            {[
                              { val: 'lead', title: 'New room',
                                desc: 'Start a fresh project — its own plan, tasks, and workspace.' },
                              { val: 'linked', title: 'Linked room',
                                desc: 'Join an existing project — share its plan + workspace; teams split work in parallel.' },
                            ].map(({ val, title, desc }) => (
                              <label key={val} className="flex cursor-pointer items-start gap-2 text-sm">
                                <input
                                  type="radio"
                                  name="roomKind"
                                  value={val}
                                  checked={roomKind === val}
                                  onChange={() => setRoomKind(val)}
                                  disabled={submitting || (val === 'linked' && linkableLeadRooms.length === 0)}
                                  className="mt-0.5"
                                />
                                <span>
                                  <span className="font-medium text-gray-800">{title}</span>
                                  <span className="block text-xs text-gray-500">
                                    {desc}
                                    {val === 'linked' && linkableLeadRooms.length === 0
                                      ? ' (no compatible Lead room on this major version yet)' : ''}
                                  </span>
                                </span>
                              </label>
                            ))}
                            {roomKind === 'linked' && linkableLeadRooms.length > 0 ? (
                              <div className="pl-6 pt-1">
                                <label className="block text-xs font-medium text-gray-700" htmlFor="leadPoolId">
                                  Link to Lead room
                                </label>
                                <select
                                  id="leadPoolId"
                                  value={leadPoolId}
                                  onChange={(ev) => setLeadPoolId(ev.target.value)}
                                  disabled={submitting}
                                  className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                  <option value="">Select a Lead room…</option>
                                  {linkableLeadRooms.map((p) => {
                                    const lbl = leadLabelOf(p);
                                    return (
                                      <option key={p.poolId} value={p.poolId}>
                                        {p.name || p.poolId}{lbl ? ` · ${lbl}` : ''}
                                      </option>
                                    );
                                  })}
                                </select>
                                <p className="mt-1 text-xs text-gray-500">
                                  Shares this Lead's workspace &amp; task ledger. Joins on the Lead's version —
                                  same major (topology); config (minor) may differ.
                                </p>
                              </div>
                            ) : null}
                          </div>
                        </fieldset>
                      ) : null}
                    </div>
                  </CollapsibleSection>

                  {/* MEMBERS — mirrors the drawer's "Members" section: pool
                      owner (CREATOR) + co-supervisors. Pre-filled (owner =
                      releaser), collapsed by default. */}
                  <CollapsibleSection
                    id="members"
                    title="Members"
                    subtitle="Pool owner · co-supervisors"
                    open={openSection.members}
                    onToggle={() => toggleSection('members')}
                    summary={
                      (ownerEmail || currentUserEmail || 'you')
                      + (supervisorEmails.length ? ` +${supervisorEmails.length}` : '')
                    }
                  >
                    <div className="space-y-4 py-1">
                      {/* Pool owner — single workspace member, defaults to the
                          releaser. Becomes the pool CREATOR; the only role that
                          can start a run at IDLE (with any co-supervisors). */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Pool owner
                        </label>
                        <p className="mt-0.5 text-xs text-gray-500">
                          Becomes the pool <span className="font-medium text-gray-700">CREATOR</span>.
                          They can manage members, change the trigger mode, and initiate runs from the room.
                        </p>
                        <div className="mt-2">
                          <SupervisorMemberPicker
                            tenantId={activeTenantId || ''}
                            value={ownerEmail}
                            onChange={(em) => setOwnerEmail(em)}
                          />
                        </div>
                      </div>
                      {/* Co-supervisors — multi workspace members. Approve /
                          deny / modify in the room; land in Members after
                          release for per-agent overrides. */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Co-supervisors
                          <span className="ml-2 text-xs font-normal text-gray-500">(optional)</span>
                        </label>
                        <p className="mt-0.5 text-xs text-gray-500">
                          Workspace members who can approve, deny or modify approvals in this pool.
                          Per-agent supervisor overrides land in <span className="font-medium">Members</span>{' '}
                          after release.
                        </p>
                        <div className="mt-2">
                          <WorkspaceMembersMultiPicker
                            tenantId={activeTenantId || ''}
                            value={supervisorEmails}
                            onChange={setSupervisorEmails}
                            excludeEmails={ownerEmail ? [ownerEmail] : []}
                            placeholder="Search workspace members…"
                            disabled={submitting}
                          />
                        </div>
                      </div>
                    </div>
                  </CollapsibleSection>

                  {/* TRIGGER MODE — mirrors the drawer's "Trigger mode"
                      section. Pre-filled (HYBRID), collapsed by default. */}
                  <CollapsibleSection
                    id="trigger"
                    title="Trigger mode"
                    subtitle="Who can start a run"
                    open={openSection.trigger}
                    onToggle={() => toggleSection('trigger')}
                    summary={
                      (triggerOptions.find((o) => o.value === triggerMode) || {}).label || triggerMode
                    }
                  >
                    <p className="mt-1 text-xs text-gray-500">
                      Who can start a run on this pool. Email approvals work in every mode.
                    </p>
                    <fieldset
                      className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3"
                      disabled={submitting}
                    >
                      <legend className="sr-only">Trigger mode</legend>
                      {triggerOptions.map((opt) => {
                        const checked = triggerMode === opt.value;
                        return (
                          <label
                            key={opt.value}
                            className={`flex flex-col gap-1 rounded-lg border p-3 text-sm cursor-pointer ${
                              checked
                                ? 'border-blue-400 bg-blue-50'
                                : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="release-trigger"
                                value={opt.value}
                                checked={checked}
                                onChange={() => setTriggerMode(opt.value)}
                              />
                              <span className="font-medium text-gray-900">{opt.label}</span>
                            </span>
                            <span className="text-xs text-gray-600">{opt.description}</span>
                          </label>
                        );
                      })}
                    </fieldset>
                  </CollapsibleSection>

                  {/* ROOM CONTEXT — mirrors the drawer's "Room context"
                      section: the room's shared setup (workspace bake +
                      own connections). Pre-filled (both on), collapsed. */}
                  <CollapsibleSection
                    id="roomContext"
                    title="Room context"
                    subtitle="Workspace · connections"
                    open={openSection.roomContext}
                    onToggle={() => toggleSection('roomContext')}
                    summary={[
                      networkWorkspaceId ? 'Network workspace'
                        : (hasBuilderAgent && bootstrapWorkspace ? 'Workspace' : null),
                      ({ off: 'No connections', room: 'Own connections',
                        network: 'Inherit connections' }[connectionMode] || 'No connections'),
                    ].filter(Boolean).join(' · ')}
                  >
                    <div className="space-y-3 py-1">
                      {hasSuggestedPlan && !(showRoomKind && roomKind === 'linked') ? (
                        <label className="flex cursor-pointer items-start gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={startWithSuggestedPlan}
                            onChange={(e) => setStartWithSuggestedPlan(e.target.checked)}
                            disabled={submitting}
                            data-testid="release-start-with-suggested-plan"
                            className="mt-0.5"
                          />
                          <span>
                            <span className="font-medium text-gray-900">Start the room with the suggested plan</span>
                            <span className="block text-xs text-gray-600">
                              The room begins with the network's suggested milestones and tasks. Its team can change
                              any of it, or replace it entirely. Leave off to let the room plan when it's ready.
                            </span>
                          </span>
                        </label>
                      ) : null}
                      {/* Pre-bake the network's skills into a workspace for the
                          new room. Only for a network with a builder agent (a
                          workspace is the builder's tree). (SKILLS.md)
                          SHARED NETWORK WORKSPACE: when the network already assigned a
                          workspace, the backend pins THAT room-wide and skips the per-room
                          bootstrap — so this control is disabled + unchecked with a note,
                          reflecting what the release actually does. */}
                      {hasBuilderAgent ? (
                        <label className={`flex items-start gap-2 text-sm ${(networkWorkspaceId || workspaceLockedToLead) ? 'cursor-default opacity-60' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            checked={!networkWorkspaceId && !workspaceLockedToLead && bootstrapWorkspace}
                            onChange={(e) => setBootstrapWorkspace(e.target.checked)}
                            disabled={submitting || !!networkWorkspaceId || workspaceLockedToLead}
                            data-testid="release-bootstrap-workspace"
                            className="mt-0.5"
                          />
                          <span>
                            <span className="font-medium text-gray-900">Set up a workspace with the network's skills</span>
                            {workspaceLockedToLead ? (
                              <span className="block text-xs text-indigo-700">
                                Disabled — a linked room shares its Lead room's workspace. That workspace is used directly; no separate room workspace is created.
                              </span>
                            ) : networkWorkspaceId ? (
                              <span className="block text-xs text-indigo-700">
                                Disabled — this network has an assigned workspace, which every room shares. That workspace is used directly; no separate room workspace is created.
                              </span>
                            ) : (
                              <span className="block text-xs text-gray-600">
                                Pre-loads this room with a workspace containing the network's skills, so builders are ready on the first run. You can edit or remove it later in the room's workspace.
                              </span>
                            )}
                          </span>
                        </label>
                      ) : null}

                      {/* How this room's connections are provisioned. Default: None (blank —
                          the operator assigns per-agent in the room's Connections tab).
                          §13: a LINKED room SHARES its Lead's connections — the backend mirrors the
                          Lead's posture, so this dial is inert. Lock it (like the workspace above)
                          with a "Shared from Lead" note instead of offering an ignored choice. */}
                      {workspaceLockedToLead ? (
                        <div className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2">
                          <div className="text-sm font-medium text-indigo-800">Room connections</div>
                          <p className="mt-1 text-xs text-indigo-700">
                            Shared from the Lead room — a linked room uses its Lead's connections
                            (credential shared; each room still enables tools per-agent). The network
                            posture isn't applied here.
                          </p>
                        </div>
                      ) : (
                      <fieldset className="space-y-1.5">
                        <legend className="text-sm font-medium text-gray-900">Room connections</legend>
                        {[
                          ['off', 'None',
                            "Start blank — no connections, and don't inherit the network's. Assign them per-agent later in the room's Connections tab."],
                          ['room', "This room's own",
                            "Give the room its own copy of the network's tools (shared + per-agent), shown in the Connections tab to credential independently."],
                          ['network', "Inherit the network's",
                            "Use the network's connections as-is (shared across its rooms)."],
                        ].map(([val, label, desc]) => (
                          <label key={val} className="flex items-start gap-2 text-sm cursor-pointer">
                            <input
                              type="radio"
                              name="connectionMode"
                              value={val}
                              checked={connectionMode === val}
                              onChange={() => setConnectionMode(val)}
                              disabled={submitting}
                              data-testid={`release-connection-mode-${val}`}
                              className="mt-0.5"
                            />
                            <span>
                              <span className="font-medium text-gray-900">{label}</span>
                              <span className="block text-xs text-gray-600">{desc}</span>
                            </span>
                          </label>
                        ))}
                      </fieldset>
                      )}
                    </div>
                  </CollapsibleSection>
                </div>
              ) : (
                <div className="mt-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Select pools to patch</span>
                    {poolsLoading ? (
                      <span className="text-xs text-gray-500">Loading…</span>
                    ) : null}
                  </div>
                  {poolsError ? (
                    <p className="text-sm text-red-600">Could not load pools: {poolsError}</p>
                  ) : null}
                  {!poolsLoading && !poolsError && patchTargets.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No existing pools to patch. Release to a new pool instead.
                    </p>
                  ) : null}
                  <ul className="max-h-48 space-y-1 overflow-y-auto">
                    {patchTargets.map((p) => {
                      const checked = selectedPoolIds.includes(p.poolId);
                      return (
                        <li key={p.poolId}>
                          <label
                            className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm cursor-pointer ${
                              checked ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => togglePoolSelection(p.poolId)}
                                disabled={submitting}
                              />
                              <span className="font-medium text-gray-900">{p.name}</span>
                              <span className="text-gray-500">({p.poolId})</span>
                            </span>
                            <span className={`text-xs ${p.isBehind ? 'text-amber-700' : 'text-gray-500'}`}>
                              {displayVersionLabel(p, versions)}
                              {p.isBehind ? ` → ${patchTargetLabel}` : ''}
                            </span>
                          </label>
                          {/* ROOM AGENT-OVERRIDE PRESERVATION (ROOM-SCOPES.md): default = keep this
                              room's own agent enable/disable/bypass across the upgrade (re-stamped to
                              the new version, bounded by what the new graph allows). Uncheck to take
                              the network's agent graph fresh. Only shown for a selected pool. */}
                          {checked ? (
                            <label className="mt-1 ml-6 flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!resetOverridePoolIds.includes(p.poolId)}
                                onChange={() => toggleKeepOverrides(p.poolId)}
                                disabled={submitting}
                              />
                              <span>Keep this room’s agent enable/disable</span>
                              {resetOverridePoolIds.includes(p.poolId) ? (
                                <span className="text-amber-700">— will reset to the network graph</span>
                              ) : null}
                            </label>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
              {!error && submitDecision.reason ? (
                <p className="mt-3 text-xs text-gray-500">{submitDecision.reason}</p>
              ) : null}

            </form>

              {/* Version history is self-collapsing + lazy (its own header toggles + the
                  releases API only fires on first expand) — no extra wrapper. */}
              <div className="mt-6">
                <ReleaseVersionHistory networkId={networkId} />
              </div>
            </div>

            {/* Pinned action bar — stays fixed at the dialog's foot; never scrolls. */}
            <div className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-200 bg-white px-6 py-4">
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                onClick={() => !submitting && onClose()}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="release-network-form"
                className={WORKBENCH_OUTLINE_BTN_MODAL_LG}
                disabled={submitting || !submitDecision.enabled}
              >
                {submitting
                  ? (selectedBehindExisting ? 'Upgrading…' : 'Releasing…')
                  : (selectedBehindExisting ? `Upgrade → ${patchTargetLabel}` : 'Release')}
              </button>
            </div>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  );
}
